import { useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@shared/auth/authContext'
import { ROLES, type UserRole } from '@shared/config/constants'
import { ROUTES } from '@shared/config/routes'
import { Button } from '@shared/ui/Button/Button'
import { NAV_CONFIG, profileNavItem } from '../model/sidebarNavConfig'
import { SidebarVideoCard } from './SidebarVideoCard'

interface Props {
  isOpen: boolean
  onClose: () => void
  isCollapsed: boolean
  onToggleCollapse: () => void
  /** Warm a route's lazy chunk on hover/focus (injected by the app layer). */
  onPrefetch?: (path: string) => void
}

const PROFILE_ROUTE: Record<UserRole, string> = {
  [ROLES.ADMIN]: ROUTES.ADMIN_PROFILE,
  [ROLES.PARTICIPANT]: ROUTES.PARTICIPANT_PERSONAL,
  [ROLES.REPRESENTATIVE]: ROUTES.REPRESENTATIVE_PROFILE,
}

const HOME_ROUTE: Record<UserRole, string> = {
  [ROLES.ADMIN]: ROUTES.ADMIN,
  [ROLES.PARTICIPANT]: ROUTES.PARTICIPANT_DASHBOARD,
  [ROLES.REPRESENTATIVE]: ROUTES.REPRESENTATIVE,
}

export function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse, onPrefetch }: Props) {
  const { role, logout } = useAuth()
  const { t } = useTranslation(['admin', 'participant', 'representative', 'common'])
  const { pathname } = useLocation()
  const navRef = useRef<HTMLElement>(null)

  // Пунктов может быть больше, чем влезает по высоте (у участника их стало
  // семь, у админа восемь), и тогда список скроллится. Активный пункт при
  // этом легко оказывается наполовину обрезанным нижней границей — подтягиваем
  // его в видимую часть, чтобы пользователь всегда видел, где находится.
  useEffect(() => {
    const active = navRef.current?.querySelector('[aria-current="page"]')
    active?.scrollIntoView({ block: 'nearest' })
  }, [pathname])

  const navItems = role ? NAV_CONFIG[role] : []
  const profileRoute = role ? PROFILE_ROUTE[role] : ROUTES.LOGIN
  const homeRoute = role ? HOME_ROUTE[role] : ROUTES.LOGIN

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'border-border bg-surface fixed z-30 flex h-full flex-col border-r transition-all duration-300',
          'lg:relative',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          isCollapsed ? 'w-[74px]' : 'w-62',
        ].join(' ')}
      >
        {/* Logo */}
        <NavLink
          to={homeRoute}
          end
          className={[
            'flex h-18 shrink-0 items-center overflow-hidden py-5',
            isCollapsed ? 'px-4' : 'px-7',
          ].join(' ')}
        >
          <img
            src="/images/logo.png"
            alt="FEMO"
            className="h-6 w-[107px] shrink-0 object-contain object-left"
          />
        </NavLink>

        {/* Nav links */}
        <nav ref={navRef} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pt-2">
          {navItems.map((item) => {
            const extraActive = item.activePaths?.some((p) => pathname.startsWith(p)) ?? false
            return (
              <NavLink
                key={item.key}
                to={item.path}
                end={
                  item.path === ROUTES.ADMIN ||
                  item.path === ROUTES.REPRESENTATIVE ||
                  item.path === ROUTES.PARTICIPANT_DASHBOARD
                }
                onClick={onClose}
                onMouseEnter={() => onPrefetch?.(item.path)}
                onFocus={() => onPrefetch?.(item.path)}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2 rounded-2xl px-4 py-2.5 transition-colors',
                    isActive || extraActive
                      ? 'bg-surface-nav text-orange'
                      : 'text-foreground hover:bg-surface-nav/50',
                    isCollapsed ? 'justify-center' : '',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <img
                      src={
                        (isActive || extraActive) && item.iconActive ? item.iconActive : item.icon
                      }
                      alt=""
                      className="size-5 min-w-5 shrink-0"
                      aria-hidden="true"
                    />
                    {!isCollapsed && (
                      <span className="text-base leading-[1.24] whitespace-nowrap">
                        {t(item.i18nKey)}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {role === ROLES.PARTICIPANT && !isCollapsed && (
          <div className="shrink-0 px-4 py-4">
            <SidebarVideoCard />
          </div>
        )}

        {/* Bottom: logout + profile */}
        <div className="flex flex-col gap-5 px-4 pb-8">
          <NavLink
            to={profileRoute}
            onClick={onClose}
            onMouseEnter={() => onPrefetch?.(profileRoute)}
            onFocus={() => onPrefetch?.(profileRoute)}
            className={({ isActive }) =>
              [
                'flex items-center gap-2 rounded-2xl px-4 py-2.5 transition-colors',
                isActive ? 'bg-surface-nav text-orange' : 'text-foreground hover:bg-surface-nav/50',
                isCollapsed ? 'justify-center' : '',
              ].join(' ')
            }
          >
            {({ isActive }) => (
              <>
                <img
                  src={isActive ? profileNavItem.iconActive : profileNavItem.icon}
                  alt=""
                  className="size-5 min-w-5 shrink-0"
                  aria-hidden="true"
                />
                {!isCollapsed && (
                  <span className="text-base leading-[1.24] whitespace-nowrap">
                    {t(profileNavItem.i18nKey)}
                  </span>
                )}
              </>
            )}
          </NavLink>

          <Button
            type="button"
            onClick={logout}
            variant="ghost"
            className={[
              'text-foreground hover:bg-surface-nav/50 h-auto w-full justify-start rounded-2xl px-4 py-2.5',
              isCollapsed ? 'justify-center' : '',
            ].join(' ')}
          >
            <svg
              className="size-5 min-w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.417 6.3c.258-3 1.8-4.225 5.175-4.225h.108c3.725 0 5.217 1.492 5.217 5.217v5.433c0 3.725-1.492 5.217-5.217 5.217h-.108c-3.35 0-4.892-1.208-5.167-4.158"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.5 10H3.017M4.875 7.208 2.083 10l2.792 2.792"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!isCollapsed && (
              <span className="text-base leading-[1.24] whitespace-nowrap">
                {t('common:logout')}
              </span>
            )}
          </Button>
        </div>

        {/* Desktop collapse toggle */}
        <Button
          type="button"
          onClick={onToggleCollapse}
          variant="ghost"
          className="border-border bg-surface absolute top-20.5 -right-3 hidden size-6 h-6 w-6 rounded-full border p-0 shadow-sm lg:flex"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </Button>
      </aside>
    </>
  )
}
